import { useEffect, useState } from 'react';
import { invoke, getSortDirectionForColumn } from './utility';

export const useTable = ({
  debugging = false,
  initialSortColumn = '',
  initialSortDirection = 'asc',
  initialPageSize = 50,
  initialPageNumber = 1,
  initialSearchParams = [],
  initialMediaState = '',
  isLoading = true,
  methods = null,
  totalItems = 0,
}) => {
  if (!methods) throw new Error('methods are required. {  }');
  const [sortColumn, setSortColumn] = useState(initialSortColumn);
  const [sortDirection, setSortDirection] = useState(initialSortDirection);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [activePage, setActivePage] = useState(initialPageNumber);
  const [searchParams, setSearchParams] = useState(initialSearchParams);
  const [mediaState, setMediaState] = useState(initialMediaState);

  const { args, dataToMerge: extraDataToMerge, fn, name } = methods;

  const data = (localValues = {}) =>
    Object.assign(
      {},
      {
        sortColumn,
        sortDirection,
        pageSize,
        pageNumber: activePage,
        searchText: searchParams,
        mediaGalleryState: mediaState,
      },
      extraDataToMerge,
      localValues,
    );

  const loadData = (localDataToMerge = {}) => {
    const mergeData = data(localDataToMerge);
    const params = args.map((arg) => mergeData[arg]);
    if (debugging)
      console.log('useTable > loadData', { fn, name, mergeData, params });
    invoke(fn, params);
  };

  const onSortClick = (column) => {
    const newSortDirection = getSortDirectionForColumn(
      sortColumn,
      column,
      sortDirection,
    );
    setSortDirection(newSortDirection);
    setSortColumn(column);
    setActivePage(1);
    if (debugging)
      console.log('useTable > onSortClick', { column, newSortDirection, data });
    loadData({
      pageNumber: 1,
      sortColumn: column,
      sortDirection: newSortDirection,
    });
  };

  const onSearch = (searchText) => {
    setSearchParams(searchText);
    setActivePage(1);
    if (debugging) console.log('useTable > onSearch', { searchText, data });
    loadData({ pageNumber: 1, searchText });
  };

  const onPageChanged = (e, d) => {
    if (d.activePage > 0) {
      setActivePage(d.activePage);
      if (debugging)
        console.log('useTable > onPageChanged', {
          activePage: d.activePage,
          d,
          data,
        });
      loadData({ pageNumber: d.activePage });
    }
  };

  const onPageSizeChanged = (size, actualSize) => {
    setPageSize(size);
    // setActualPageSize(actualSize);
    setActivePage(1);
    if (debugging) console.log('useTable > onPageSizeChanged', { size, data });
    loadData({ pageNumber: 1, pageSize: size });
  };

  const getActualPageSize = () => {
    if (totalItems === 0) return initialPageSize;

    const morePagesExist = activePage * pageSize < totalItems;
    const remainingRowsToView = pageSize - (activePage * pageSize - totalItems);

    return morePagesExist ? pageSize : remainingRowsToView;
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (debugging)
      console.log('useTable > onMount > change', {
        sortDirection,
        sortColumn,
        pageSize,
        activePage,
        searchParams,
        methods,
        isLoading,
      });
  }, [
    sortDirection,
    sortColumn,
    pageSize,
    activePage,
    searchParams,
    methods,
    isLoading,
  ]);

  if (debugging)
    console.log('useTable > getActualPageSize', getActualPageSize());

  return {
    loadData,
    activePage,
    actualPageSize: getActualPageSize(),
    onPageChanged,
    onPageSizeChanged,
    onSearch,
    onSortClick,
    pageSize,
    setSortColumn,
    setSortDirection,
    sortDirection,
  };
};
